import { Injectable, OnDestroy, ElementRef } from '@angular/core';

interface ModalRegistration {
  elementRef: ElementRef;
  closeCallback: () => void;
  mouseDownInside: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ModalClickOutsideService implements OnDestroy {
  private modals: Map<ElementRef, ModalRegistration> = new Map();
  private mouseUpHandler?: (e: MouseEvent) => void;
  private mouseDownHandler?: (e: MouseEvent) => void;
  private keyDownHandler?: (e: KeyboardEvent) => void;
  private initialized = false;

  constructor() {
    this.initializeHandlers();
  }

  private initializeHandlers(): void {
    if (this.initialized) return;
    this.initialized = true;

    // Handler pour mousedown - détecter si le clic commence dans le modal
    this.mouseDownHandler = (e: MouseEvent) => {
      this.modals.forEach((registration, elementRef) => {
        const modalContentEl = elementRef.nativeElement;
        if (!modalContentEl) return;

        // Vérifier que l'élément est toujours dans le DOM
        if (!document.body.contains(modalContentEl)) {
          this.unregisterModal(elementRef);
          return;
        }

        // Vérifier si le mousedown est à l'intérieur du modal
        const isInside = modalContentEl.contains(e.target as Node);
        registration.mouseDownInside = isInside;
      });
    };

    // Handler pour mouseup - fermer seulement si mousedown ET mouseup sont à l'extérieur
    this.mouseUpHandler = (e: MouseEvent) => {
      // Traiter les modals dans l'ordre inverse (le plus récent en premier)
      const modalsArray = Array.from(this.modals.entries()).reverse();
      
      for (const [elementRef, registration] of modalsArray) {
        const modalContentEl = elementRef.nativeElement;
        if (!modalContentEl) continue;

        // Vérifier que l'élément est toujours dans le DOM
        if (!document.body.contains(modalContentEl)) {
          this.unregisterModal(elementRef);
          continue;
        }

        // Vérifier si le mouseup est à l'extérieur
        const clickedOutside = !modalContentEl.contains(e.target as Node);
        
        // Fermer seulement si mousedown ET mouseup sont à l'extérieur
        if (clickedOutside && !registration.mouseDownInside) {
          registration.closeCallback();
          // Arrêter après avoir fermé le premier modal trouvé
          break;
        }
        
        // Réinitialiser pour le prochain clic
        registration.mouseDownInside = false;
      }
    };

    // Handler pour la touche Echap
    this.keyDownHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        // Trouver le dernier modal enregistré (le plus récent) et le fermer
        const modalsArray = Array.from(this.modals.entries());
        if (modalsArray.length > 0) {
          const lastModal = modalsArray[modalsArray.length - 1];
          const modalContentEl = lastModal[0].nativeElement;
          
          // Vérifier que l'élément est toujours dans le DOM
          if (modalContentEl && document.body.contains(modalContentEl)) {
            lastModal[1].closeCallback();
          } else {
            this.unregisterModal(lastModal[0]);
          }
        }
      }
    };

    document.addEventListener('mousedown', this.mouseDownHandler, true);
    document.addEventListener('mouseup', this.mouseUpHandler, true);
    document.addEventListener('keydown', this.keyDownHandler);
  }

  /**
   * Enregistre un modal pour la gestion du clic extérieur
   * @param elementRef Référence à l'élément du contenu du modal
   * @param closeCallback Fonction à appeler pour fermer le modal
   */
  registerModal(elementRef: ElementRef, closeCallback: () => void): void {
    if (!elementRef) return;
    
    // Utiliser setTimeout pour s'assurer que le DOM est mis à jour
    setTimeout(() => {
      const modalContentEl = elementRef.nativeElement;
      if (!modalContentEl) {
        // Réessayer une fois de plus si l'élément n'est pas encore disponible
        setTimeout(() => {
          const modalContentEl2 = elementRef.nativeElement;
          if (modalContentEl2 && document.body.contains(modalContentEl2)) {
            this.modals.set(elementRef, { 
              elementRef, 
              closeCallback, 
              mouseDownInside: false 
            });
            this.initializeHandlers();
          }
        }, 50);
        return;
      }
      
      // Vérifier que l'élément est dans le DOM
      if (document.body.contains(modalContentEl)) {
        this.modals.set(elementRef, { 
          elementRef, 
          closeCallback, 
          mouseDownInside: false 
        });
        this.initializeHandlers();
      } else {
        // Réessayer une fois de plus si l'élément n'est pas encore dans le DOM
        setTimeout(() => {
          const modalContentEl3 = elementRef.nativeElement;
          if (modalContentEl3 && document.body.contains(modalContentEl3)) {
            this.modals.set(elementRef, { 
              elementRef, 
              closeCallback, 
              mouseDownInside: false 
            });
            this.initializeHandlers();
          }
        }, 50);
      }
    }, 0);
  }

  /**
   * Désenregistre un modal
   * @param elementRef Référence à l'élément du contenu du modal
   */
  unregisterModal(elementRef: ElementRef): void {
    if (!elementRef) return;
    this.modals.delete(elementRef);
  }

  ngOnDestroy(): void {
    if (this.mouseUpHandler) {
      document.removeEventListener('mouseup', this.mouseUpHandler, true);
    }
    if (this.mouseDownHandler) {
      document.removeEventListener('mousedown', this.mouseDownHandler, true);
    }
    if (this.keyDownHandler) {
      document.removeEventListener('keydown', this.keyDownHandler);
    }
    this.modals.clear();
    this.initialized = false;
  }
}

